import React, { useEffect, useState } from "react";
import { useMode } from "../lib/mode";
import { listDecisions, getPatterns } from "../lib/api";
import { format } from "date-fns";
import { TrendingUp, Activity, Layers } from "lucide-react";

export default function FounderDecisionMap({ onOpen }) {
  const { memberToken } = useMode();
  const [decisions, setDecisions] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!memberToken) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [d, p] = await Promise.all([
          listDecisions({ member_token: memberToken }),
          getPatterns(memberToken),
        ]);
        if (!active) return;
        setDecisions(d || []);
        setPatterns(p);
      } catch (e) {
        // silent
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [memberToken]);

  return (
    <section className="space-y-8" data-testid="founder-decision-map">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#f4b847] mb-2">Founder Decision Map</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-medium text-[#1A1A1A]">Your decisions, connecting.</h2>
          <p className="text-[#666666] mt-2">Pin this. Come back to it. Patterns get clearer with every entry.</p>
        </div>
      </div>

      {/* Pattern cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pisa-enter-stagger">
        <div className="pisa-card p-6" data-testid="pattern-total">
          <div className="flex items-center gap-2 mb-3 text-[#666666]">
            <Layers className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Total decisions</span>
          </div>
          <div className="text-4xl font-medium text-[#1A1A1A]">{patterns?.total_decisions ?? 0}</div>
          <p className="text-sm text-[#666666] mt-2">Captured in your map</p>
        </div>
        <div className="pisa-card p-6" data-testid="pattern-pressure">
          <div className="flex items-center gap-2 mb-3 text-[#666666]">
            <Activity className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Avg pressure</span>
          </div>
          <div className="text-4xl font-medium text-[#1A1A1A]">{patterns?.avg_pressure ?? 0}<span className="text-lg text-[#666666]">/100</span></div>
          <div className="pisa-bar mt-3"><span style={{ width: `${patterns?.avg_pressure ?? 0}%`, background: "#e93e36" }} /></div>
        </div>
        <div className="pisa-card p-6" data-testid="pattern-bias">
          <div className="flex items-center gap-2 mb-3 text-[#666666]">
            <TrendingUp className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Avg bias lean</span>
          </div>
          <div className="text-4xl font-medium text-[#1A1A1A]">{patterns?.avg_bias ?? 0}<span className="text-lg text-[#666666]">/100</span></div>
          <div className="pisa-bar mt-3"><span style={{ width: `${patterns?.avg_bias ?? 0}%`, background: "#2596be" }} /></div>
        </div>
      </div>

      {/* Scenario breakdown + common assumptions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="pisa-card p-7">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-4">Decision types</p>
          {(patterns?.scenario_counts || []).length === 0 ? (
            <p className="text-sm text-[#666666]">Your map fills in as you use PISA.</p>
          ) : (
            <ul className="space-y-3" data-testid="scenario-list">
              {patterns.scenario_counts.map((s, i) => {
                const max = patterns.scenario_counts[0].count;
                const pct = (s.count / max) * 100;
                return (
                  <li key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-[#1A1A1A]">{s.label}</span>
                      <span className="text-sm text-[#666666]">{s.count}</span>
                    </div>
                    <div className="pisa-bar"><span style={{ width: `${pct}%`, background: "#2596be" }} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="pisa-card p-7">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666] mb-4">Repeated assumptions</p>
          {(patterns?.common_assumptions || []).length === 0 ? (
            <p className="text-sm text-[#666666]">As you log more decisions, recurring assumptions surface here.</p>
          ) : (
            <ul className="space-y-2" data-testid="common-assumptions">
              {patterns.common_assumptions.map((a, i) => (
                <li key={i} className="text-sm text-[#1A1A1A] flex gap-2"><span className="text-[#f4b847]">·</span>{a}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Decision history */}
      <div className="pisa-card p-7">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#666666]">Decision history</p>
          <span className="text-xs text-[#666666]">{decisions.length} saved</span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 py-6"><span className="pisa-dot" /><span className="pisa-dot" /><span className="pisa-dot" /></div>
        ) : decisions.length === 0 ? (
          <p className="text-sm text-[#666666] py-4">No decisions yet. Make one above — it will appear here.</p>
        ) : (
          <ul className="divide-y divide-[#E5E5E5]" data-testid="decision-history">
            {decisions.map((d) => (
              <li key={d.id} className="py-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#2596be]/10 text-[#2596be]">{d.scenario_type}</span>
                    <span className="text-xs text-[#666666]">{format(new Date(d.created_at), "MMM d, yyyy")}</span>
                  </div>
                  <p className="text-sm text-[#1A1A1A] truncate">{d.decision_prompt}</p>
                  <p className="text-xs text-[#666666] mt-1 line-clamp-2">{d.brief?.decision_now}</p>
                </div>
                <button onClick={() => onOpen?.(d)} className="text-sm text-[#2596be] hover:text-[#1E7D9F] whitespace-nowrap" data-testid={`open-decision-${d.id}`}>Open →</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
